<template>
    <section class="component discussion-profile-modes-page" style="position: relative; flex-grow: 1;">
        <section class="component__header">
            <section class="discussion-profile-modes-page__header-copy">
                <span class="component__header__title">
                    {{ $t("com.discussion.profile.modes") }}
                </span>
                <span class="discussion-profile-modes-page__subtitle">
                    {{ profileName }}
                </span>
            </section>
            <section class="component__header__action">
                <ButtonOutline color="light" type="sm" @click="openCreateModal" :disable="isCreateDisabled">
                    <ButtonIcon icon="plus-small" invert />
                    {{ $t("com.global.add") }}
                </ButtonOutline>
                <ButtonOutline color="light" type="sm" @click="goBack">
                    {{ $t("com.global.back") }}
                </ButtonOutline>
            </section>
        </section>

        <section class="component__body">
            <div class="table-bootstrap-responsive" v-if="hasModes">
                <table class="table-bootstrap table-bootstrap-hover table-bootstrap-bordered table-bootstrap-striped align-middle">
                    <thead>
                        <tr class="align-middle">
                            <th>{{ $t("com.discussion.profile.mode-type") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-block") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-first-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-second-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-third-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-next-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-turn-off") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-sound-notification") }}</th>
                            <th style="width: 20px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="mode in modes" :key="mode.id">
                            <td>{{ mode.type || "-" }}</td>
                            <td class="align-middle text-center">{{ mode.isBlock ? $t("com.global.yes") : $t("com.global.no") }}</td>
                            <td class="align-middle text-center">{{ getStatementTimeLabel(mode.firstStatement) }}</td>
                            <td class="align-middle text-center">{{ getStatementTimeLabel(mode.secondStatement) }}</td>
                            <td class="align-middle text-center">{{ getStatementTimeLabel(mode.thirdStatement) }}</td>
                            <td class="align-middle text-center">{{ getStatementTimeLabel(mode.nextStatement) }}</td>
                            <td class="align-middle text-center">{{ mode.turnOff ? $t("com.global.yes") : $t("com.global.no") }}</td>
                            <td class="align-middle text-center">{{ mode.isSoundNotification ? $t("com.global.yes") : $t("com.global.no") }}</td>
                            <td>
                                <ButtonGroup>
                                    <ButtonOutline color="light" type="sm" @click="openEditModal(mode)">
                                        <ButtonIcon icon="edit" />
                                        {{ $t("com.global.edit") }}
                                    </ButtonOutline>
                                    <ButtonOutline color="danger" type="sm" @click="deleteMode(mode)">
                                        <ButtonIcon icon="delete" />
                                        {{ $t("com.global.delete") }}
                                    </ButtonOutline>
                                </ButtonGroup>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <section v-else-if="!isLoading" class="text-center">
                {{ $t("com.global.no-data") }}
            </section>
        </section>

        <SmallLoader v-if="isLoading || isSaving" />
        <AddDiscussionProfileModeModal
            v-if="isShowCreateModeModal"
            :discussion-id="discussionId"
            :statement-types="statementTypes"
            :statement-time-options="statementTimeOptions"
            :used-type-ids="usedTypeIds"
            @close="closeCreateModal"
            @created="handleModeCreated"
        />
        <EditDiscussionProfileModeModal
            v-if="isShowEditModeModal && selectedMode"
            :discussion-id="discussionId"
            :mode="selectedMode"
            :statement-types="statementTypes"
            :statement-time-options="statementTimeOptions"
            :used-type-ids="usedTypeIdsForEdit"
            @close="closeEditModal"
            @updated="handleModeUpdated"
        />
    </section>
</template>

<script charset="utf-8">
import AddDiscussionProfileModeModal from "@/components/Panel/Content/DiscussionProfile/Modals/AddDiscussionProfileModeModal.vue";
import EditDiscussionProfileModeModal from "@/components/Panel/Content/DiscussionProfile/Modals/EditDiscussionProfileModeModal.vue";
import SmallLoader from "@/components/BasicControls/SmallLoader.vue";
import AlertHelper from "@/helpers/alerts.helper.js";
import DiscussionProfileService from "@/services/discussion.profile.service";

export default {
    name: "DiscussionProfileModesPage",
    components: {
        AddDiscussionProfileModeModal,
        EditDiscussionProfileModeModal,
        SmallLoader
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,
            isShowCreateModeModal: false,
            isShowEditModeModal: false,
            modes: [],
            profile: null,
            selectedMode: null,
            statementTypes: [],
            statementTimeOptions: [
                { textKey: "com.discussion.profile.mode-time.unlimited", id: -1 },
                { textKey: "com.discussion.profile.mode-time.blocked", id: -2 },
                { textKey: "com.discussion.profile.mode-time.10-seconds", id: 10 },
                { textKey: "com.discussion.profile.mode-time.20-seconds", id: 20 },
                { textKey: "com.discussion.profile.mode-time.30-seconds", id: 30 },
                { textKey: "com.discussion.profile.mode-time.40-seconds", id: 40 },
                { textKey: "com.discussion.profile.mode-time.50-seconds", id: 50 },
                { textKey: "com.discussion.profile.mode-time.1-minute", id: 60 },
                { textKey: "com.discussion.profile.mode-time.1-5-minutes", id: 90 },
                { textKey: "com.discussion.profile.mode-time.2-minutes", id: 120 },
                { textKey: "com.discussion.profile.mode-time.3-minutes", id: 180 },
                { textKey: "com.discussion.profile.mode-time.4-minutes", id: 240 },
                { textKey: "com.discussion.profile.mode-time.5-minutes", id: 300 },
                { textKey: "com.discussion.profile.mode-time.6-minutes", id: 360 },
                { textKey: "com.discussion.profile.mode-time.7-minutes", id: 420 },
                { textKey: "com.discussion.profile.mode-time.8-minutes", id: 480 },
                { textKey: "com.discussion.profile.mode-time.9-minutes", id: 540 },
                { textKey: "com.discussion.profile.mode-time.10-minutes", id: 600 },
                { textKey: "com.discussion.profile.mode-time.15-minutes", id: 900 },
                { textKey: "com.discussion.profile.mode-time.20-minutes", id: 1200 },
                { textKey: "com.discussion.profile.mode-time.25-minutes", id: 1500 },
                { textKey: "com.discussion.profile.mode-time.30-minutes", id: 1800 },
                { textKey: "com.discussion.profile.mode-time.40-minutes", id: 2400 },
                { textKey: "com.discussion.profile.mode-time.45-minutes", id: 2700 },
                { textKey: "com.discussion.profile.mode-time.50-minutes", id: 3000 },
                { textKey: "com.discussion.profile.mode-time.1-hour", id: 3600 }
            ]
        };
    },
    computed: {
        discussionId() {
            return Number(this.$route.params.discussionId);
        },
        hasModes() {
            return Array.isArray(this.modes) && this.modes.length > 0;
        },
        profileName() {
            return this.profile?.name || this.$t("com.discussion.discussion-profile");
        },
        usedTypeIds() {
            return this.modes
                .map((mode) => Number(mode.typeId))
                .filter((typeId) => Number.isFinite(typeId));
        },
        usedTypeIdsForEdit() {
            if (!this.selectedMode?.id) {
                return this.usedTypeIds;
            }

            return this.modes
                .filter((mode) => mode.id !== this.selectedMode.id)
                .map((mode) => Number(mode.typeId))
                .filter((typeId) => Number.isFinite(typeId));
        },
        isCreateDisabled() {
            return this.isLoading || this.statementTypes.length === 0 || this.usedTypeIds.length >= this.statementTypes.length;
        }
    },
    async mounted() {
        await this.loadPage();
    },
    methods: {
        async loadPage() {
            if (!Number.isFinite(this.discussionId) || this.discussionId <= 0) {
                return;
            }

            try {
                this.isLoading = true;

                const [profileResponse, modesResponse, statementTypesResponse] = await Promise.all([
                    DiscussionProfileService.GetById(this.discussionId),
                    DiscussionProfileService.GetModes(this.discussionId),
                    DiscussionProfileService.GetStatementTypes()
                ]);

                this.profile = profileResponse.data ?? null;
                this.modes = Array.isArray(modesResponse.data) ? modesResponse.data : [];
                this.statementTypes = Array.isArray(statementTypesResponse.data) ? statementTypesResponse.data : [];

                if (this.profile) {
                    this.$store.commit("setSelectedDiscussionProfile", this.profile);
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
            }
        },
        getStatementTimeLabel(value) {
            const option = this.statementTimeOptions.find((item) => item.id === value);
            return option ? this.$t(option.textKey) : value;
        },
        openCreateModal() {
            if (this.isCreateDisabled) {
                return;
            }

            this.isShowCreateModeModal = true;
        },
        closeCreateModal() {
            this.isShowCreateModeModal = false;
        },
        async handleModeCreated() {
            await this.loadPage();
        },
        openEditModal(mode) {
            this.selectedMode = { ...mode };
            this.isShowEditModeModal = true;
        },
        closeEditModal() {
            this.isShowEditModeModal = false;
            this.selectedMode = null;
        },
        async handleModeUpdated() {
            this.closeEditModal();
            await this.loadPage();
        },
        alertInfo() {
            let info = "";

            info += `<p>${this.$t("com.global.confirmation-alert")}</p>`;
            info += `<p>${this.$t("com.discussion.profile.remove-mode-confirmation")}</p>`;

            return info;
        },
        createAlertData() {
            return {
                mainText: this.alertInfo(),
                buttons: [
                    {
                        id: 1,
                        text: this.$t("com.global.yes"),
                        color: "#d80418"
                    }
                ]
            };
        },
        async deleteMode(mode) {
            const actionToFire = async () => {
                if (!mode?.id) {
                    return;
                }

                try {
                    this.isSaving = true;
                    await DiscussionProfileService.DeleteMode(this.discussionId, mode.id);
                    this.closeEditModal();
                    await this.loadPage();
                } catch (e) {
                    if (e.response !== undefined) {
                        const t = this.$swal.mixin(AlertHelper.errorToastElement());
                        t.fire(
                            AlertHelper.errorToastAlert(
                                this.$t("com.global.default-save-error"),
                                this.$t("com.global.error")
                            )
                        );
                    }
                } finally {
                    this.isSaving = false;
                }
            };

            this.$store.commit("setAlertData", this.createAlertData());
            this.$store.commit("showConfirmationAlertWithData", actionToFire);
        },
        goBack() {
            this.$router.push({
                name: "client-settings-discussion-profile"
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/user-profile-component.scss";

.discussion-profile-modes-page {
    &__header-copy {
        display: flex;
        flex-direction: column;
        row-gap: 4px;
    }

    &__subtitle {
        color: #808396;
        font-size: 14px;
        line-height: 1.4;
    }

    .component__header__action {
        display: flex;
        flex-direction: row;
        column-gap: 12px;
    }

    .component__body{
        min-height: 0;
    }
}
</style>
